import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import pageConfig from "@/utils/pageConfig";

const TIANDITU_KEY = pageConfig.globalConfig.TIANDITU_KEY;
const TIANDITU_SCRIPT_ID = pageConfig.globalConfig.TIANDITU_SCRIPT_ID;

const DEFAULT_CENTER = {
  lng: 116.40769,
  lat: 39.9042,
};

declare global {
  interface Window {
    T: any;
  }
}

export type SelectedLocation = {
  longitude: string;
  latitude: string;
  address: string;
};

export interface MapLocationPickerProps {
  open: boolean;
  initialLocation: Partial<SelectedLocation>;
  onCancel: () => void;
  onConfirm: (location: SelectedLocation) => void;
}

const loadTianDiTuScript = () =>
  new Promise<void>((resolve, reject) => {
    if (window.T) {
      resolve();
      return;
    }

    const existedScript = document.getElementById(
      TIANDITU_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existedScript) {
      existedScript.addEventListener('load', () => resolve(), { once: true });
      existedScript.addEventListener(
        'error',
        () => reject(new Error('天地图脚本加载失败')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = TIANDITU_SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TIANDITU_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('天地图脚本加载失败'));
    document.head.appendChild(script);
  });

const sanitizeAddress = (address: string) =>
  address
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
    .slice(0, 200);

const formatAddressFromResult = (result: any) => {
  if (!result) {
    return '';
  }

  if (typeof result.getAddress === 'function') {
    return sanitizeAddress(String(result.getAddress() || ''));
  }

  if (result.address) {
    return sanitizeAddress(String(result.address));
  }

  if (result.formatted_address) {
    return sanitizeAddress(String(result.formatted_address));
  }

  const comp = result.addressComponent || result.addressComponents;
  if (!comp) {
    return '';
  }

  return sanitizeAddress(
    [
      comp.province,
      comp.city,
      comp.county,
      comp.town,
      comp.street,
      comp.streetNumber,
    ]
      .filter(Boolean)
      .join(''),
  );
};

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  open,
  initialLocation,
  onCancel,
  onConfirm,
}) => {
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation | null>(null);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);

  const setMarker = (lng: number, lat: number) => {
    if (!mapRef.current || !window.T) {
      return;
    }

    const lnglat = new window.T.LngLat(lng, lat);

    if (!markerRef.current) {
      markerRef.current = new window.T.Marker(lnglat);
      mapRef.current.addOverLay(markerRef.current);
    } else {
      markerRef.current.setLngLat(lnglat);
    }

    mapRef.current.panTo(lnglat);
  };

  const reverseGeocode = (lng: number, lat: number) => {
    if (!window.T) {
      return;
    }

    const lnglat = new window.T.LngLat(lng, lat);
    const fallback = {
      longitude: lng.toFixed(6),
      latitude: lat.toFixed(6),
      address: '',
    };

    if (!geocoderRef.current) {
      setSelectedLocation(fallback);
      return;
    }

    geocoderRef.current.getLocation(lnglat, (result: any) => {
      const address = formatAddressFromResult(result);
      setSelectedLocation({
        longitude: fallback.longitude,
        latitude: fallback.latitude,
        address,
      });
    });
  };

  const initMap = async () => {
    try {
      setMapLoading(true);
      await loadTianDiTuScript();

      if (!mapContainerRef.current || !window.T) {
        return;
      }

      if (!mapRef.current) {
        mapRef.current = new window.T.Map(mapContainerRef.current);
        geocoderRef.current = new window.T.Geocoder();
        mapRef.current.addEventListener('click', (e: any) => {
          const lng = e?.lnglat?.getLng?.();
          const lat = e?.lnglat?.getLat?.();
          if (typeof lng !== 'number' || typeof lat !== 'number') {
            return;
          }
          setMarker(lng, lat);
          reverseGeocode(lng, lat);
        });
      }

      const formLng = Number(initialLocation?.longitude);
      const formLat = Number(initialLocation?.latitude);
      const lng =
        Number.isFinite(formLng) && formLng !== 0 ? formLng : DEFAULT_CENTER.lng;
      const lat =
        Number.isFinite(formLat) && formLat !== 0 ? formLat : DEFAULT_CENTER.lat;

      mapRef.current.centerAndZoom(new window.T.LngLat(lng, lat), 14);

      if (
        Number.isFinite(formLng) &&
        Number.isFinite(formLat) &&
        formLng !== 0 &&
        formLat !== 0
      ) {
        setMarker(formLng, formLat);
        setSelectedLocation({
          longitude: formLng.toFixed(6),
          latitude: formLat.toFixed(6),
          address: String(initialLocation?.address || ''),
        });
      } else {
        setSelectedLocation(null);
      }
    } catch (error) {
      console.error(error);
      message.error('天地图加载失败，请稍后重试');
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      initMap();
    }
  }, [open]);

  const handleConfirm = () => {
    if (!selectedLocation) {
      message.warning('请先在地图上点击选择定位点');
      return;
    }
    onConfirm(selectedLocation);
  };

  return (
    <Modal
      title="地图定位"
      open={open}
      width={880}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="确认定位"
      cancelText="取消" 
    >
      <Spin spinning={mapLoading} tip="地图加载中...">
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: 460,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
          }}
        />
      </Spin>
      <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.65)' }}>
        {selectedLocation
          ? `当前选点：${selectedLocation.longitude}, ${selectedLocation.latitude}${selectedLocation.address ? `（${selectedLocation.address}）` : ''}`
          : '请在地图上点击要定位的位置'}
      </div>
    </Modal>
  );
};

export default MapLocationPicker;
